import React, { useState, useEffect, useMemo, useCallback } from "react";
import { engagementName, statusBadge as statusBadgeFor } from "@/lib/engagementLabel";   // Batch 8.4 · A3 (F-54); Batch 14 · M13 / M14
import EngagementPicker, { useRememberRecentEngagement } from "@/components/EngagementPicker";   // Step 11b · N1 (labels, lock, search)
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, FileUp, BarChart3, Sparkles, ClipboardList,
  FileSearch, AlertTriangle, SlidersHorizontal, FileText, Bot, LogOut,
  Languages, ChevronDown, ChevronRight, History, ShieldAlert, Percent,
  Building2, Globe, KeyRound, ClipboardCheck, FolderOpen, CheckSquare,
  BookOpen, Wallet, ShieldCheck, MessageSquareWarning,
  ArrowUp, ArrowLeft, Package, Compass, TrendingUp, Hammer, FileBarChart,
  LayoutGrid, Users, Inbox, Zap, Server, HardDrive, Mail, Activity, Send,
  Network, X,
  Handshake, Crosshair, Route, LockKeyhole, Footprints, ServerCog,
  ListChecks, OctagonAlert, FolderTree, NotebookTabs, RotateCcw,
  SquareCheckBig, Sigma, NotebookPen, Stamp, FilePenLine, Archive,
  Trash2, DraftingCompass,   // Batch 22 · Add-on 1: the 203(a) nav item
  Wrench,
  Lock, BookOpenCheck,
  BellRing,   // STEP 10 · E: Founder → Alerts
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import MethodologyVersionBadge from "@/components/methodology/MethodologyVersionBadge";   // STEP 7e-1 · M3
import { confirmLeave } from "@/lib/unsavedChanges";   // Batch 28 · Follow-up 1 · F2
import SessionExpiryWarning from "@/components/SessionExpiryWarning";
import SavedCopyFrame from "@/components/SavedCopyFrame";   // Batch 29 · M69   // Batch 28 · Follow-up 1 · F2 (b) safeguard 5
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { layoutCtx } from "./_layoutCtx";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import NotificationsBell from "@/components/NotificationsBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import SearchBar from "@/components/SearchBar";
import PresenceDock from "@/components/PresenceDock";
import RestoreMissingBanner from "@/components/RestoreMissingBanner";   // Batch 15 · N6 (F-120)
import EngagementLocaleBanner from "@/components/EngagementLocaleBanner";   // PR 45 review · D2
import TimeChip from "@/components/TimeChip";
import { firmZone, firmZoneAbbr, fmtDate } from "@/lib/dt";
import { ChevronsLeft, ChevronsRight, ChevronsUp, ChevronsDown, Menu, Maximize2, Minimize2, Expand } from "lucide-react";
import { useFullScreenMode, browserFullScreenAvailable, toggleBrowserFullScreen } from "@/lib/fullScreenMode";   // Batch 23 · Stage 6(b)

// -------- Role vocabulary (unchanged from prior nav) --------
const MGMT_ROLES = ["Admin", "Audit Manager", "Partner", "Reviewer",
                     "Supervisor", "Assistant Manager", "Manager", "Senior Manager"];
const PARTNER_ONLY = ["Admin", "Partner"];
const RP_VIEWER_ROLES = ["Admin", "Partner", "Senior Manager", "Manager",
                          "Assistant Manager", "Supervisor", "Reviewer",
                          "Audit Manager", "EQC", "Preparer", "Auditor"];
const AI_USAGE_ROLES = ["Partner", "Audit Manager"];

// -------- Nav model: 7 groups + Dashboard pinned above --------
// Presentation-only regrouping — every existing `to`, `testid`, role
// gate, and label is preserved verbatim from the prior flat NAV list.
const DASHBOARD = {
  to: "/", key: "nav.dashboard",
  icon: LayoutDashboard, testid: "nav-dashboard", end: true,
};

// Platform-Admin-only pinned row (kept as its own pinned entry, above
// Dashboard — the previous flat-nav also put it first for that role).
const PLATFORM_PIN = {
  to: "/platform", key: "nav.platform", icon: Globe, testid: "nav-platform",
  roles: ["Platform Admin"], emphasize: true,
};

// Platform-Admin section sub-nav — rendered inside the dark-blue main
// sidebar directly under PLATFORM_PIN when the user is on /platform.
// Each entry deep-links via ?tab= so the section nav can also be reached
// from anywhere in the app. Labels/testids kept in exact sync with
// pages/Platform.jsx's SECTIONS array (both derived from the same set).
const PLATFORM_SECTIONS = [
  { id: "tenants",            testid: "sidebar-platform-tenants",            labelKey: "layoutNav.platform.tenants",            icon: Building2 },
  { id: "users",              testid: "sidebar-platform-users",              labelKey: "layoutNav.platform.users",              icon: Users },
  { id: "plans",              testid: "sidebar-platform-plans",              labelKey: "layoutNav.platform.plans",              icon: Package },
  { id: "requests",           testid: "sidebar-platform-reqs",               labelKey: "layoutNav.platform.plan_requests",      icon: Inbox },
  { id: "ai-keys",            testid: "sidebar-platform-ai-keys",            labelKey: "layoutNav.platform.ai_keys",            icon: Zap },
  { id: "worker-rollout",     testid: "sidebar-platform-worker-rollout",     labelKey: "layoutNav.platform.worker_rollout",     icon: Server },
  { id: "worker-diagnostics", testid: "sidebar-platform-worker-diagnostics", labelKey: "layoutNav.platform.worker_diagnostics", icon: Activity },
  { id: "storage",            testid: "sidebar-platform-storage",            labelKey: "layoutNav.platform.storage_health",     icon: HardDrive },
  // Round 7 · P1 — the Email Provider tab was registered in
  // pages/Platform.jsx's SECTIONS array but NEVER appeared in the
  // sidebar sub-nav list here, so the only way to reach it was by
  // typing `?tab=email-provider` by hand. The founder's sidebar
  // therefore showed every other platform tab except the one he
  // actually needed. Adding it here renders the switch under the
  // Platform-Admin pinned row for the SIDE layout, and the TOP-layout
  // horizontal strip in pages/Platform.jsx has always listed it.
  { id: "email-provider",     testid: "sidebar-platform-email-provider",     labelKey: "layoutNav.platform.email_provider",     icon: Send },
  { id: "diagnostics",        testid: "sidebar-platform-diagnostics",        labelKey: "layoutNav.platform.diagnostics",        icon: Mail },
  { id: "errors",             testid: "sidebar-platform-errors",             labelKey: "layoutNav.platform.errors",             icon: AlertTriangle },   // 5b · R72
  { id: "alerts",             testid: "sidebar-platform-alerts",             labelKey: "s10e.alerts.nav",                       icon: BellRing },   // STEP 10 · E
  { id: "saved-copies",       testid: "sidebar-platform-saved-copies",       labelKey: "layoutNav.platform.saved_copies",       icon: HardDrive },   // Batch 29 · M69
  { id: "recycle-bin",        testid: "sidebar-platform-recycle-bin",        labelKey: "layoutNav.platform.recycle_bin",        icon: Trash2 },
  { id: "maintenance",        testid: "sidebar-platform-maintenance",        labelKey: "layoutNav.platform.maintenance",        icon: Wrench },   // Batch 7
  { id: "package-keys",       testid: "sidebar-platform-package-keys",       labelKey: "layoutNav.platform.package_keys",       icon: LockKeyhole },   // Batch 14 · M15
];

export const NAV_GROUPS = [   // exported for the search bar test (Batch 28 · item 10)
  {
    id: "engagement_setup", labelKey: "layoutNav.groups.engagement_setup", icon: Package,
    testid: "nav-group-engagement-setup", items: [
      { to: "/engagements", key: "nav.engagements", icon: Briefcase, testid: "nav-engagements" },
      { to: "/methodology", labelKey: "layoutNav.items.methodology", icon: BookOpen, testid: "nav-methodology" },
    ],
  },
  {
    id: "acceptance", labelKey: "layoutNav.groups.acceptance", icon: Handshake,
    testid: "nav-group-acceptance", items: [
      { to: "/acceptance", labelKey: "layoutNav.items.acceptance", icon: Handshake, testid: "nav-acceptance" },
      { to: "/review/w17-1-acceptance", labelKey: "w17_1.title", icon: Handshake, testid: "nav-w17-1-acceptance",
        reviewOnly: true },
    ],
  },
  {
    id: "planning", labelKey: "layoutNav.groups.planning", icon: Compass,
    testid: "nav-group-planning", items: [
      { to: "/gl-import", key: "nav.glImport", icon: FileUp, testid: "nav-gl" },
      { to: "/planning", labelKey: "layoutNav.items.planning", icon: ClipboardCheck, testid: "nav-planning",
        pctTab: "planning" },
      { to: "/analytics", key: "nav.analytics", icon: BarChart3, testid: "nav-analytics" },
      { to: "/ratios", key: "nav.ratios", icon: Percent, testid: "nav-ratios" },
      { to: "/scoping", labelKey: "layoutNav.items.scoping", icon: Crosshair, testid: "nav-scoping" },
    ],
  },
  {
    id: "risk_assessment", labelKey: "layoutNav.groups.risk_assessment", icon: ShieldAlert,
    testid: "nav-group-risk-assessment", items: [
      { to: "/risk-assessment-forms", labelKey: "layoutNav.items.risk_assessment_forms", icon: FolderOpen,
        testid: "nav-risk-forms", pctTab: "risk_assessment_forms" },
      { to: "/risk", key: "nav.risk", icon: ShieldAlert, testid: "nav-risk" },
      { to: "/fieldwork/a9001", labelKey: "layoutNav.items.risk_a9001_pointer",
        icon: ShieldAlert, testid: "nav-risk-a9001-pointer" },
      { to: "/review/w17-2-understanding", labelKey: "w17_2.title", icon: ShieldAlert,
        testid: "nav-w17-2-understanding", reviewOnly: true },
    ],
  },
  {
    id: "digital_audit", labelKey: "layoutNav.groups.digital_audit", icon: Network,
    testid: "nav-group-digital-audit", items: [
      { to: "/digital-audit/correlation", labelKey: "layoutNav.items.correlation",
        icon: Network, testid: "nav-digital-audit-correlation" },
      { to: "/digital-audit/sub-ledger", labelKey: "layoutNav.items.sub_ledger_receivable",
        icon: Network, testid: "nav-digital-audit-sub-ledger" },
      { to: "/digital-audit/trade-payable", labelKey: "layoutNav.items.sub_ledger_payable",
        icon: Network, testid: "nav-digital-audit-trade-payable" },
      { to: "/digital-audit/inventory", labelKey: "layoutNav.items.sub_ledger_inventory",
        icon: Network, testid: "nav-digital-audit-inventory" },
    ],
  },
  {
    id: "audit_response", labelKey: "layoutNav.groups.audit_response", icon: Route,
    testid: "nav-group-audit-response", items: [
      { to: "/audit-response", labelKey: "layoutNav.items.audit_response", icon: Route, testid: "nav-audit-response" },
    ],
  },
  {
    id: "controls", labelKey: "layoutNav.groups.controls", icon: LockKeyhole,
    testid: "nav-group-controls", items: [
      // Batch 22 · Add-on 1 (item 2): 203(a) Control Design first — design comes before walkthroughs in the audit flow
      { to: "/controls/203a", labelKey: "layoutNav.items.controls_203a",
        icon: DraftingCompass, testid: "nav-controls-203a" },
      { to: "/controls/walkthroughs", labelKey: "layoutNav.items.controls_walkthroughs",
        icon: Footprints, testid: "nav-controls-walkthroughs" },
      { to: "/controls/itgc", labelKey: "layoutNav.items.controls_itgc",
        icon: ServerCog, testid: "nav-controls-itgc" },
      { to: "/controls/a9002", labelKey: "layoutNav.items.controls_a9002",
        icon: ListChecks, testid: "nav-controls-a9002" },
      { to: "/controls/deficiency-log", labelKey: "layoutNav.items.controls_deficiency_log",
        icon: OctagonAlert, testid: "nav-controls-deficiency-log" },
    ],
  },
  {
    id: "fieldwork", labelKey: "layoutNav.groups.fieldwork", icon: Hammer,
    testid: "nav-group-fieldwork", items: [
      { to: "/sampling", key: "nav.sampling", icon: Sparkles, testid: "nav-sampling" },
      { to: "/vouching", key: "nav.vouching", icon: ClipboardList, testid: "nav-vouching" },
      { to: "/confirmations", labelKey: "layoutNav.items.confirmations", icon: ClipboardCheck, testid: "nav-confirmations" },
      { to: "/documents", key: "nav.docs", icon: FileSearch, testid: "nav-documents" },
      { to: "/doc-summaries", key: "nav.docSummaries", icon: FileSearch, testid: "nav-doc-summaries" },
      { to: "/discrepancies", key: "nav.discrepancies", icon: AlertTriangle, testid: "nav-discrepancies" },
      { to: "/head-folders", labelKey: "layoutNav.items.head_folders",
        icon: FolderTree, testid: "nav-head-folders" },
      { to: "/fieldwork/a9001", labelKey: "layoutNav.items.fieldwork_a9001",
        icon: NotebookTabs, testid: "nav-fieldwork-a9001" },
      { to: "/fieldwork/a9003", labelKey: "layoutNav.items.fieldwork_a9003",
        icon: RotateCcw, testid: "nav-fieldwork-a9003" },
      // Batch 13 · AE5 — Adjusting entries: the last audit item of Fieldwork
      { to: "/fieldwork/adjusting-entries", labelKey: "layoutNav.items.fieldwork_adjusting_entries",
        icon: BookOpenCheck, testid: "nav-fieldwork-adjusting-entries" },
      // K2 — all five W17.3–W17.7 review pages live under Fieldwork
      // in the 503(r) → 507(r) order; each `reviewOnly:true` so
      // they never surface on an audit engagement.
      { to: "/review/w17-3-analytical", labelKey: "w17_3.title", icon: BarChart3,
        testid: "nav-w17-3-analytical", reviewOnly: true },
      { to: "/review/w17-4-inquiries", labelKey: "w17_4.title", icon: ClipboardList,
        testid: "nav-w17-4-inquiries", reviewOnly: true },
      { to: "/review/w17-5-specific", labelKey: "w17_5.title", icon: ShieldAlert,
        testid: "nav-w17-5-specific", reviewOnly: true },
      { to: "/review/w17-6-reconciliation", labelKey: "w17_6.title", icon: NotebookPen,
        testid: "nav-w17-6-reconciliation", reviewOnly: true },
      { to: "/review/w17-7-register", labelKey: "w17_7.title", icon: ListChecks,
        testid: "nav-w17-7-register", reviewOnly: true },
    ],
  },
  {
    id: "completion", labelKey: "layoutNav.groups.completion", icon: SquareCheckBig,
    testid: "nav-group-completion", items: [
      { to: "/completion/misstatements", labelKey: "layoutNav.items.misstatement_worksheet",
        icon: Sigma, testid: "nav-misstatements" },
      { to: "/review/w17-8-conclusion", labelKey: "w17_8.title", icon: SquareCheckBig,
        testid: "nav-w17-8-conclusion", reviewOnly: true },
      { to: "/completion", labelKey: "layoutNav.items.completion", icon: CheckSquare, testid: "nav-completion",
        pctTab: "completion" },
      // Batch 33 · B33-1: the final analytical review as its own item (316(a) on audit, R316(a) on review)
      { to: "/final-analytical-review", labelKey: "layoutNav.items.final_analytical_review", icon: TrendingUp,
        testid: "nav-final-analytical-review" },
      { to: "/review-points", labelKey: "layoutNav.items.review_points", icon: MessageSquareWarning,
        testid: "nav-review-points", roles: RP_VIEWER_ROLES },
      { to: "/completion/summary-memo", labelKey: "layoutNav.items.audit_summary_memo",
        reviewLabelKey: "layoutNav.items.review_summary_memo",
        icon: NotebookPen, testid: "nav-audit-summary-memo" },
      { to: "/completion/eq-review", labelKey: "layoutNav.items.eq_review_link",
        icon: ShieldCheck, testid: "nav-eq-review-link", eqcOnly: true },   // Batch 32 · MT3: ISQM 2 on EQC engagements only
      { to: "/completion/clearance-gate", labelKey: "layoutNav.items.clearance_gate",
        icon: Stamp, testid: "nav-clearance-gate" },
      { to: "/completion/kam-register", labelKey: "layoutNav.items.kam_register", icon: ClipboardCheck, testid: "nav-kam-register" },
      { to: "/completion/other-information", labelKey: "layoutNav.items.other_information", icon: FileText, testid: "nav-other-information" },
    ],
  },
  {
    id: "reporting", labelKey: "layoutNav.groups.reporting", icon: FileBarChart,
    testid: "nav-group-reporting", items: [
      { to: "/ifrs", key: "nav.ifrs", icon: FileText, testid: "nav-ifrs" },
      { to: "/disclosure-checklist", labelKey: "layoutNav.items.disclosure_checklist", icon: ClipboardCheck, testid: "nav-disclosure-checklist", pctFrom: "dc" },
      { to: "/reporting/auditors-report", labelKey: "layoutNav.items.auditors_report",
        icon: FilePenLine, testid: "nav-auditors-report" },
      { to: "/reporting/firm-report-formats", labelKey: "layoutNav.items.firm_report_formats", icon: FileText, testid: "nav-firm-report-formats",
        roles: ["Partner"] },
    ],
  },
  {
    id: "close_archive", labelKey: "layoutNav.groups.close_archive", icon: Archive,
    testid: "nav-group-close-archive", items: [
      { to: "/close/file-assembly", labelKey: "layoutNav.items.file_assembly",
        icon: Lock, testid: "nav-file-assembly" },
      { to: "/verify-bundle", labelKey: "layoutNav.items.verify_bundle", icon: ShieldCheck, testid: "nav-verify-bundle",
        roles: PARTNER_ONLY },
      { to: "/audit-trail", key: "nav.auditTrail", icon: History, testid: "nav-audit-trail" },
      { to: "/recycle-bin", labelKey: "layoutNav.items.recycle_bin", icon: Trash2, testid: "nav-recycle-bin" },
    ],
  },
  {
    id: "governance", labelKey: "layoutNav.groups.governance", icon: ShieldCheck,
    testid: "nav-group-governance", items: [
      { to: "/isqm", labelKey: "layoutNav.items.isqm", icon: ShieldCheck, testid: "nav-isqm",
        isqmGated: true, pctFrom: "isqm" },
      { to: "/document-library", labelKey: "layoutNav.items.doc_library", icon: BookOpen, testid: "nav-doclib" },
    ],
  },
  {
    id: "workspace", labelKey: "layoutNav.groups.workspace", icon: LayoutGrid,
    testid: "nav-group-workspace", items: [
      // Stage 2 · 2.3 ONE DASHBOARD: the Executive dashboard item is gone — its figures live on the pinned Dashboard
      // (pages/FirmDashboard.jsx); /executive redirects there (App.js).
      { to: "/assistant", key: "nav.assistant", icon: Bot, testid: "nav-assistant" },
      { to: "/billing", labelKey: "layoutNav.items.billing", icon: Wallet, testid: "nav-billing",
        roles: PARTNER_ONLY },
      { to: "/ai-usage", labelKey: "layoutNav.items.ai_usage", icon: Bot, testid: "nav-ai-usage",
        roles: AI_USAGE_ROLES },
      { to: "/settings", key: "nav.settings", icon: SlidersHorizontal, testid: "nav-settings",
        roles: MGMT_ROLES },
      { to: "/help", labelKey: "layoutNav.items.help", icon: BookOpen, testid: "nav-help" },
    ],
  },
];

// Batch E · Round E1 — audit-only pages that are greyed on Review
// engagements. Route paths match `w17.AUDIT_ONLY_PAGES` on the backend.
const AUDIT_ONLY_ROUTES = new Set([
  "/audit-response",
  "/controls/203a",   // Batch 22 · Add-on 1 (item 5)
  "/controls/walkthroughs", "/controls/itgc", "/controls/a9002",
  "/controls/deficiency-log",
  "/sampling", "/vouching", "/confirmations",
  "/fieldwork/a9001", "/fieldwork/a9003", "/fieldwork/adjusting-entries",
  "/risk",
  // M6 — Reporting → Auditor's report is audit-only on a Review
  // engagement (ISRE 2400 (Revised) gives the practitioner's
  // limited-assurance report, not an audit opinion).
  "/reporting/auditors-report",
]);

const EXPANDED_LS_KEY = "af_nav_expanded_groups";
const LAYOUT_LS_KEY = "af_nav_layout";

// -------- Helpers --------
function labelOf(item, t, ctx) {
  // H6 — on Review engagements prefer `reviewLabelKey` when provided.
  if (ctx && ctx.engagementType === "review" && item.reviewLabelKey) {
    const v = t(item.reviewLabelKey);
    if (v) return v;
  }
  if (item.labelKey) return t(item.labelKey);
  return item.label || (item.key ? t(item.key) : item.to);
}
function groupLabelOf(g, t) {
  return g.labelKey ? t(g.labelKey) : g.label;
}
const dcPctRef = { value: null };
function pctBadgeColor(p) {
  if (p == null) return "";
  if (p >= 80) return "bg-emerald-500 text-white";
  if (p >= 40) return "bg-amber-500 text-white";
  return "bg-slate-500 text-white";
}
function itemPct(item, formsProgress, isqmPct) {
  if (item.pctTab) return formsProgress[item.pctTab]?.pct ?? null;
  if (item.pctFrom === "isqm") return isqmPct;
  if (item.pctFrom === "dc") return dcPctRef.value;
  return null;
}
function groupMinPct(group, formsProgress, isqmPct, visibleFilter) {
  let m = null;
  for (const it of group.items) {
    if (!visibleFilter(it)) continue;
    const p = itemPct(it, formsProgress, isqmPct);
    if (p == null) continue;
    m = m == null ? p : Math.min(m, p);
  }
  return m;
}

// -------- Group node used by both layouts --------
function ItemLink({ item, t, formsProgress, isqmPct, onNavigate, compact = false,
                        greyed = false, reviewEnabled = false }) {
  const pct = itemPct(item, formsProgress, isqmPct);
  const tip = greyed ? t("review.notUsed") : (reviewEnabled ? t("review.pageEnabledOk") : undefined);
  return (
    <NavLink to={item.to} end={item.end} data-testid={item.testid}
      onClick={onNavigate}
      title={tip}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md ps-9 pe-3 py-2 text-sm transition-colors ${
          isActive ? "font-semibold" : "hover:bg-white/5"
        } ${greyed ? "opacity-50" : ""}`
      }
      style={({ isActive }) => (isActive
        ? { backgroundColor: "rgba(186,148,74,0.18)", color: "#BA944A", borderLeft: "3px solid #BA944A" }
        : { color: "#E8DFC5" })}>
      <item.icon className="h-4 w-4" strokeWidth={1.75} />
      <span className="flex-1">{labelOf(item, t, {engagementType: layoutCtx.engagementType})}</span>
      {greyed && (
        <span data-testid={`${item.testid}-greyed`}
              className="rounded-full bg-slate-700/50 px-1.5 py-0.5 text-[9px] font-medium text-slate-300">
          🔒 {t("review.notUsed")}
        </span>
      )}
      {reviewEnabled && (
        <span data-testid={`${item.testid}-review-enabled`}
              className="rounded-full bg-emerald-700/40 px-1.5 py-0.5 text-[9px] font-medium text-emerald-200">
          {t("review.enabledBadge")}
        </span>
      )}
      {pct != null && !greyed && (
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${pctBadgeColor(pct)}`}
              data-testid={`sidebar-pct-${item.pctTab || item.pctFrom}`}>{pct}%</span>
      )}
    </NavLink>
  );
}

// -------- Sidebar (Side layout) --------
function SidebarGroups({
  visibleGroups, expandedGroups, toggleGroup, t, formsProgress, isqmPct,
  visibleItem, activeGroupId, isItemGreyed, isItemReviewEnabled,
}) {
  return visibleGroups.map((g) => {
    const isOpen = expandedGroups.has(g.id);
    const minPct = !isOpen ? groupMinPct(g, formsProgress, isqmPct, visibleItem) : null;
    const isActiveGroup = activeGroupId === g.id;
    return (
      <div key={g.id} className="mb-1" data-testid={g.testid}>
        <button type="button" onClick={() => toggleGroup(g.id)}
          data-testid={`${g.testid}-header`}
          className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-[13px] font-bold uppercase tracking-wider transition-colors ${
            isActiveGroup ? "" : "hover:bg-white/5"
          }`}
          style={isActiveGroup
            ? { color: "#BA944A", backgroundColor: "rgba(186,148,74,0.10)" }
            : { color: "#E8DFC5" }}>
          {isOpen
            ? <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
            : <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />}
          <g.icon className="h-4 w-4" style={{ color: "#BA944A" }} strokeWidth={2} />
          <span className="flex-1 text-start">{groupLabelOf(g, t)}</span>
          {!isOpen && minPct != null && (
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${pctBadgeColor(minPct)}`}
                   data-testid={`${g.testid}-agg-pct`}
                   title={t("layoutNav.chrome.group_min_pct_tooltip")}>{minPct}%</span>
          )}
        </button>
        {isOpen && (
          <div className="mt-0.5 space-y-0.5" data-testid={`${g.testid}-content`}>
            {g.items.filter(visibleItem).map((it) => (
              <ItemLink key={it.testid} item={it} t={t}
                        formsProgress={formsProgress} isqmPct={isqmPct}
                        greyed={isItemGreyed ? isItemGreyed(it) : false}
                        reviewEnabled={isItemReviewEnabled ? isItemReviewEnabled(it) : false} />
            ))}
          </div>
        )}
      </div>
    );
  });
}

// -------- Top layout — horizontal pill dropdowns --------
function TopGroupPill({ group, t, formsProgress, isqmPct, visibleItem, isActive,
                              isItemGreyed, isItemReviewEnabled }) {
  const [open, setOpen] = useState(false);
  const minPct = groupMinPct(group, formsProgress, isqmPct, visibleItem);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button type="button"
          data-testid={`${group.testid}-top`}
          className={`inline-flex shrink-0 items-center gap-2 rounded-md px-3.5 py-2 text-[15px] font-bold tracking-tight transition-colors ${
            isActive
              ? "bg-slate-900 text-white shadow-sm dark:bg-slate-100 dark:text-slate-900"
              : "text-slate-900 hover:bg-emerald-50 hover:text-emerald-700 dark:text-slate-100 dark:hover:bg-slate-800"
          }`}>
          <group.icon className={`h-[18px] w-[18px] ${isActive ? "" : "text-emerald-600"}`} strokeWidth={2} />
          {groupLabelOf(group, t)}
          {minPct != null && (
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${pctBadgeColor(minPct)}`}
                   data-testid={`${group.testid}-top-agg-pct`}>{minPct}%</span>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-70" strokeWidth={2.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[260px]">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {groupLabelOf(group, t)}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {group.items.filter(visibleItem).map((it) => (
          <TopDropdownItem key={it.testid} item={it} t={t}
                            formsProgress={formsProgress} isqmPct={isqmPct}
                            greyed={isItemGreyed ? isItemGreyed(it) : false}
                            reviewEnabled={isItemReviewEnabled ? isItemReviewEnabled(it) : false}
                            onNavigate={() => setOpen(false)} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
function TopDropdownItem({ item, t, formsProgress, isqmPct, onNavigate,
                                    greyed = false, reviewEnabled = false }) {
  const pct = itemPct(item, formsProgress, isqmPct);
  return (
    <DropdownMenuItem asChild data-testid={`${item.testid}-top`}>
      <NavLink to={item.to} end={item.end} onClick={onNavigate}
                title={greyed ? t("review.notUsed") : undefined}
                className={`flex items-center gap-2 text-sm font-medium ${greyed ? "opacity-50" : ""}`}>
        <item.icon className="h-4 w-4 opacity-80" strokeWidth={1.75} />
        <span className="flex-1">{labelOf(item, t, {engagementType: layoutCtx.engagementType})}</span>
        {greyed && (
          <span data-testid={`${item.testid}-top-greyed`}
                 className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-semibold text-slate-700">
            🔒 {t("review.notUsed")}
          </span>
        )}
        {reviewEnabled && (
          <span data-testid={`${item.testid}-top-review-enabled`}
                 className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-800">
            {t("review.enabledBadge")}
          </span>
        )}
        {pct != null && !greyed && (
          <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${pctBadgeColor(pct)}`}>{pct}%</span>
        )}
      </NavLink>
    </DropdownMenuItem>
  );
}

// -------- Layout root --------
// Batch 32 · MT3: an ISQM 2 (EQ review) item shows only on an engagement flagged "Requires EQC review"
export const navItemEqcOk = (n, current) => !n.eqcOnly || !!current?.requires_eqc;

export default function Layout() {
  const { user, org, logout } = useAuth();
  // Batch 23 · Stage 6(b): Full screen — the sidebar and the top bar hide; the choice is remembered per user (try/catch storage)
  const [fullScreen, setFullScreen, toggleFullScreen] = useFullScreenMode(user?.user_id);
  const { t, lang, toggle } = useI18n();
  const { engagements, currentEngId, setCurrentEngId, current } = useEngagement();
  useRememberRecentEngagement(user?.user_id || user?.id, currentEngId);   // Step 11b · N1: the picker's "Recent" list
  // J5 — publish the engagement type so module-scoped NavItem/GroupItem
  // subcomponents can show review wording for W10 (etc.) without a
  // per-render prop chain.
  layoutCtx.engagementType = current?.engagement_type;
  const [, setDcPct] = useState(null);
  useEffect(() => {
    if (!currentEngId) { dcPctRef.value = null; setDcPct(null); return undefined; }
    const loadDc = () => api.get(`/disclosure-checklist/${currentEngId}/progress`, { _silent: true })
      .then((r) => { dcPctRef.value = r.data?.percent ?? null; setDcPct(dcPctRef.value); }).catch(() => {});
    loadDc();
    const iv = setInterval(loadDc, 60000);
    const onEvt = () => loadDc();
    window.addEventListener("dc-progress-changed", onEvt);
    return () => { clearInterval(iv); window.removeEventListener("dc-progress-changed", onEvt); };
  }, [currentEngId]);
  const nav = useNavigate();
  const location = useLocation();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [formsProgress, setFormsProgress] = useState({});
  const [isqmAccess, setIsqmAccess] = useState(null);
  const [isqmPct, setIsqmPct] = useState(null);
  const mustChange = !!user?.must_change_password;

  // ---------- Layout preference ----------
  // Prefer server-persisted value if we have it; fall back to
  // localStorage so a returning user sees their preferred layout
  // instantly on first paint (before the /auth/me hydration).
  // Batch 19 · Stage C (C3): on a phone the sidebar is an off-canvas drawer opened from the top bar; it closes on navigation
  const [phoneNavOpen, setPhoneNavOpen] = useState(false);
  useEffect(() => { setPhoneNavOpen(false); }, [location.pathname, location.search]);
  const [layoutMode, setLayoutMode] = useState(() => {
    try {
      const cached = localStorage.getItem(LAYOUT_LS_KEY);
      return cached === "top" ? "top" : "side";
    } catch { return "side"; }
  });
  useEffect(() => {
    const p = user?.preferences?.nav_layout;
    if (p === "side" || p === "top") {
      setLayoutMode(p);
      try { localStorage.setItem(LAYOUT_LS_KEY, p); } catch { /* noop */ }
    }
  }, [user?.preferences?.nav_layout]);
  const persistLayout = useCallback(async (next) => {
    setLayoutMode(next);
    try { localStorage.setItem(LAYOUT_LS_KEY, next); } catch { /* noop */ }
    // Notify listeners that don't receive layoutMode as a prop —
    // notably pages/Platform.jsx, which hides its on-page section nav
    // when this is "side" (sidebar owns nav) and shows a horizontal
    // strip when this is "top" (sidebar is a top bar with no room for
    // sub-items). A custom event is cheaper than lifting state to
    // context and doesn't require every consumer to opt in.
    try {
      window.dispatchEvent(
        new CustomEvent("af-nav-layout-change", { detail: next }));
    } catch { /* noop */ }
    try {
      await api.patch("/auth/me/preferences", { nav_layout: next });
    } catch {
      /* server persist is best-effort — the localStorage copy still
         holds this session and any others on the same device. */
    }
  }, []);

  // ---------- Password gate ----------
  useEffect(() => { if (mustChange) setPwdOpen(true); }, [mustChange]);

  // ---------- ISQM sidebar visibility + firm-wide pct ----------
  // Step 7c · E3: the parts of user it reads. Layout renders only while signed in (App.js Protected) and the old key
  // user?.user_id is never set on the /auth/me user (UserPublic has `id`), so this ran once per mount and still does.
  const signedIn = !!user;
  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    api.get("/isqm/my-access", { _silent: true })
      .then((r) => { if (alive) setIsqmAccess(r.data); })
      .catch(() => { if (alive) setIsqmAccess({ is_partner: false, firm_isqm1: false, engagement_isqm2: [] }); });
    const loadPct = () => (user?.role === "Platform Admin" ? Promise.resolve() : api.get("/isqm/completion/firm", { _silent: true }))   // 5c · F-7: not a firm user
      .then((r) => { if (alive) setIsqmPct(r.data?.overall?.pct ?? null); })
      .catch(() => {});
    loadPct();
    const iv = setInterval(loadPct, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [signedIn, user?.role]);

  // ---------- Forms progress polling ----------
  useEffect(() => {
    if (!currentEngId) { setFormsProgress({}); return; }
    let alive = true;
    const load = () => api.get(`/forms/${currentEngId}/progress`, { _silent: true })
      .then((r) => { if (alive) setFormsProgress(r.data?.per_tab || {}); })
      .catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [currentEngId]);

  // ---------- Batch E · E1 — engagement profile (grey-not-hide) --------
  const [engProfile, setEngProfile] = useState(null);
  useEffect(() => {
    if (!currentEngId) { setEngProfile(null); return; }
    let alive = true;
    api.get(`/engagements/${currentEngId}/profile`, { _silent: true })
      .then(r => { if (alive) setEngProfile(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [currentEngId]);

  // ---------- Visibility filters ----------
  const currentRequiresEqc = !!current?.requires_eqc;   // Batch 32 · MT3
  const visibleItem = useCallback((n) => {
    if (n.roles && !n.roles.includes(user?.role)) return false;
    if (n.isqmGated) {
      if (!isqmAccess) return false;
      return !!isqmAccess.firm_isqm1 || (isqmAccess.engagement_isqm2 || []).length > 0;
    }
    // Batch E · E1 — sidebar items marked `reviewOnly` render only on
    // Review engagements; on Audit engagements they are hidden (D2/D3
    // regression rule).
    if (n.reviewOnly) {
      return (engProfile?.engagement_type || "audit") === "review";
    }
    if (!navItemEqcOk(n, { requires_eqc: currentRequiresEqc })) return false;   // Batch 32 · MT3
    return true;
  }, [user?.role, isqmAccess, engProfile?.engagement_type, currentRequiresEqc]);

  // Batch 28 · item 10 (M55): the search bar suggests the sidebar's own pages (only those this user sees here)
  const searchNavGroups = useMemo(() => NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(visibleItem) })), [visibleItem]);

  // Grey-not-hide: on a Review engagement, audit-only items are shown
  // greyed instead of hidden. Layout renderer applies opacity + a lock
  // marker for greyed items and an "Enabled" marker for enabled ones.
  const isItemGreyed = useCallback((n) => {
    if (!engProfile || engProfile.engagement_type !== "review") return false;
    if (!AUDIT_ONLY_ROUTES.has(n.to)) return false;
    const enab = (engProfile.page_enablements || {})[n.to];
    return !(enab && enab.enabled === true);
  }, [engProfile]);
  const isItemReviewEnabled = useCallback((n) => {
    if (!engProfile || engProfile.engagement_type !== "review") return false;
    if (!AUDIT_ONLY_ROUTES.has(n.to)) return false;
    const enab = (engProfile.page_enablements || {})[n.to];
    return !!(enab && enab.enabled === true);
  }, [engProfile]);

  const visibleGroups = useMemo(() => {
    // Platform Admins get a stripped-down sidebar — only Dashboard
    // (rendered separately as DASHBOARD) and the Platform pin remain
    // visible. Every audit-workflow NAV_GROUP is hidden for this role
    // by design (their view is /platform, not tenant-scoped audit work).
    if (user?.role === "Platform Admin") return [];
    return NAV_GROUPS
      .map((g) => ({ ...g, items: g.items.filter(visibleItem) }))
      .filter((g) => g.items.length > 0);
  }, [visibleItem, user?.role]);

  // ---------- Group expand/collapse state ----------
  const initialExpanded = useMemo(() => {
    // Restore last state, but the active group is ALWAYS forced open
    // so a user never lands on a page whose item is hidden.
    let cached;
    try { cached = JSON.parse(localStorage.getItem(EXPANDED_LS_KEY) || ""); }
    catch { cached = null; }
    if (Array.isArray(cached)) return new Set(cached);
    // Default: only the group containing the active route is open.
    return new Set(visibleGroups.map((g) => g.id));
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const [expandedGroups, setExpandedGroups] = useState(initialExpanded);

  const activeGroupId = useMemo(() => {
    // Match either an exact path or a proper subpath (`/planning/*`).
    // Guard against loose prefix matches like `/planning-notes` falsely
    // hitting `/planning` — the trailing "/" or exact equality is the
    // canonical route boundary.
    const p = location.pathname;
    for (const g of NAV_GROUPS) {
      if (g.items.some((it) => p === it.to
                                 || (it.to !== "/" && p.startsWith(it.to + "/")))) {
        return g.id;
      }
    }
    return null;
  }, [location.pathname]);

  // On route change, force the active group open so the current item
  // is never hidden — regardless of the last expand/collapse-all state.
  useEffect(() => {
    if (!activeGroupId) return;
    setExpandedGroups((prev) => {
      if (prev.has(activeGroupId)) return prev;
      const next = new Set(prev);
      next.add(activeGroupId);
      return next;
    });
  }, [activeGroupId]);

  // Persist expand/collapse across sessions.
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_LS_KEY, JSON.stringify(Array.from(expandedGroups)));
    } catch { /* noop */ }
  }, [expandedGroups]);

  const toggleGroup = (id) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const expandAll = () => setExpandedGroups(new Set(visibleGroups.map((g) => g.id)));
  const collapseAll = () => {
    // Keep the active group open, per spec: "the currently-active
    // item's parent group must still auto-expand".
    setExpandedGroups(new Set(activeGroupId ? [activeGroupId] : []));
  };

  // ---------- Brand palette (fixed — Ivory & Emerald, cannot be tenant-overridden) ----------
  // Emerald  = sidebar body + primary CTA fill
  // Gold     = active nav state + accents
  // Ivory    = light backgrounds
  const EMERALD = "#0A3A2E";
  const GOLD    = "#BA944A";
  const primary = EMERALD;
  const secondary = GOLD;
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoBroken, setLogoBroken] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  useEffect(() => {
    setLogoBroken(false);
    setLogoSrc(null);
    const u = org?.logo_url;
    if (!u) return;
    if (u.startsWith("http")) { setLogoSrc(u); return; }
    const m = u.match(/documents\/file\/([^/?]+)/);
    if (!m) { setLogoSrc(null); return; }
    let cancelled = false;
    import("@/lib/api").then(({ getDownloadUrl }) =>
      getDownloadUrl(m[1], { mode: "preview" })
        .then((full) => { if (!cancelled) setLogoSrc(full); })
        .catch(() => { if (!cancelled) setLogoBroken(true); }));
    return () => { cancelled = true; };
  }, [org?.logo_url]);

  const isPlatformAdmin = user?.role === "Platform Admin";

  // Direct arrow-toggle button — icon reflects the target state so the
  // user always sees where the click will take them.
  // Batch 3 · 3.7 (R34) — collapsible navigation (left sidebar → icons; top bar → hidden), remembered per browser.
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem("af_nav_collapsed") === "1"; } catch { return false; }
  });
  const toggleNavCollapsed = () => setNavCollapsed((v) => {
    try { localStorage.setItem("af_nav_collapsed", v ? "0" : "1"); } catch { /* ignore */ }
    return !v;
  });
  const CollapseButton = ({ variant }) => {
    const Icon = layoutMode === "top" ? (navCollapsed ? ChevronsDown : ChevronsUp) : (navCollapsed ? ChevronsRight : ChevronsLeft);
    return (
      <button type="button" onClick={toggleNavCollapsed}
        title={navCollapsed ? t("layoutNav.chrome.expand_nav") : t("layoutNav.chrome.collapse_nav")}
        data-testid="nav-collapse-toggle"
        className={variant === "header"
          ? "inline-flex h-8 w-8 items-center justify-center rounded-md border text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
          : "inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"}>
        <Icon className="h-4 w-4" strokeWidth={2} />
      </button>
    );
  };

  const ToggleButton = ({ variant }) => {
    const targetTop = layoutMode === "side";
    const Icon = targetTop ? ArrowUp : ArrowLeft;
    const bg = { backgroundColor: primary };
    const commonCls = "inline-flex h-14 w-14 items-center justify-center rounded-full text-white shadow-md ring-1 ring-white/20 hover:brightness-110 transition-all";
    return (
      <button type="button" style={bg}
        onClick={() => persistLayout(targetTop ? "top" : "side")}
        title={targetTop ? t("layoutNav.chrome.switch_to_top") : t("layoutNav.chrome.switch_to_side")}
        data-testid="sidebar-layout-toggle"
        className={variant === "top" ? `${commonCls} ms-1` : commonCls}>
        <Icon className="h-8 w-8" strokeWidth={2.5} />
      </button>
    );
  };

  // Batch 15 · N6 (F-120): the sidebar header — logo + firm name as before; the engagement name on its own line that
  // wraps (never cut); ONE small status badge under it (lock + "Report Final dd-MMM-yyyy" / "Locked" / "Restored").
  // The "not included" note of a restored package is a banner at the top of the page content (RestoreMissingBanner).
  const statusBadge = statusBadgeFor(current, t, fmtDate);
  const engagementLine = current ? (
    <div className="mt-1.5 min-w-0" data-testid="sidebar-engagement">
      <div className="whitespace-normal break-words text-[12px] font-medium leading-snug text-white/95" data-testid="sidebar-engagement-name">{engagementName(current, t)}</div>
      {statusBadge && (
        <span className="mt-1 inline-flex max-w-full items-center gap-1 rounded border border-amber-300/60 bg-amber-100/15 px-1.5 py-0.5 text-[10px] text-amber-100" data-testid="sidebar-engagement-badge">
          {statusBadge.lock && <Lock className="h-3 w-3 shrink-0 text-amber-300" data-testid="sidebar-engagement-lock" />}
          <span className="truncate" title={statusBadge.text} data-testid="sidebar-engagement-lock-label">{statusBadge.text}</span>
        </span>
      )}
    </div>
  ) : null;
  const brandBadge = (
    <div className="flex items-center gap-2 min-w-0">
      <button type="button"
        onClick={() => setLogoOpen(true)}
        title={t("layoutNav.chrome.view_logo")}
        data-testid="sidebar-logo-open"
        className="shrink-0 rounded-md ring-1 ring-white/15 transition hover:ring-white/40 hover:brightness-110">
        {logoSrc && !logoBroken ? (
          <img src={logoSrc} alt="logo" className="h-12 w-12 rounded-md object-cover"
                onError={() => setLogoBroken(true)} />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-md overflow-hidden"
               style={{ backgroundColor: EMERALD }}>
            <img src="/azex-logo.png" alt={t("layoutNav.chrome.azex_logo_alt")} className="h-11 w-11 object-contain" />
          </div>
        )}
      </button>
      <div className="min-w-0">
        <div className="break-words text-sm font-semibold tracking-tight text-white" title={org?.name || t("app.name")}>{org?.name || t("app.name")}</div>
        <div className="text-[10px]" style={{ color: "#C9BFA1" }}>{t("app.tagline")}</div>
      </div>
    </div>
  );

  const logoLightbox = logoOpen ? (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
         onClick={() => setLogoOpen(false)}
         data-testid="logo-lightbox">
      <button type="button"
        onClick={() => setLogoOpen(false)}
        aria-label={t("layoutNav.chrome.close_aria")}
        className="absolute end-6 top-6 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        data-testid="logo-lightbox-close">
        <X className="h-6 w-6" />
      </button>
      <img
        src={logoSrc && !logoBroken ? logoSrc : "/azex-logo.png"}
        alt={t("layoutNav.chrome.brand_logo_alt")}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[80vh] max-w-[80vw] rounded-lg object-contain shadow-2xl"
        data-testid="logo-lightbox-img"
      />
    </div>
  ) : null;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#F7F4EC] dark:bg-slate-950"
         style={{ ["--brand-primary"]: primary, ["--brand-accent"]: secondary }}>

      {/* SIDE LAYOUT */}
      {layoutMode === "side" && !fullScreen && (
        <aside className={`app-sidebar relative flex h-screen shrink-0 flex-col overflow-hidden ${navCollapsed ? "w-16 sidebar-collapsed" : "w-64"} ${phoneNavOpen ? "phone-drawer-open" : ""}`}
                style={{ backgroundColor: EMERALD, color: "#E8DFC5" }} data-testid="sidebar" data-collapsed={navCollapsed ? "1" : "0"}>
          <div className={`border-b ${navCollapsed ? "px-2 py-3" : "px-5 py-4"}`} style={{ borderColor: "rgba(186,148,74,0.25)" }} data-testid="sidebar-header">
            <div className={`flex items-center gap-2 ${navCollapsed ? "flex-col" : ""}`}>
              {!navCollapsed && brandBadge}
              <div className={navCollapsed ? "" : "ms-auto"}><CollapseButton /></div>
              {!navCollapsed && <ToggleButton />}
            </div>
            {!navCollapsed && engagementLine}{/* Batch 15 · N6: the engagement on its own line, full sidebar width */}
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
            {/* Platform-Admin-only pinned row */}
            {isPlatformAdmin && (
              <NavLink to={PLATFORM_PIN.to} data-testid={PLATFORM_PIN.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-base font-semibold transition-colors ${
                    isActive ? "" : "hover:bg-white/5"
                  }`}
                style={({ isActive }) => (isActive
                  ? { backgroundColor: "rgba(186,148,74,0.18)", color: GOLD, borderLeft: `3px solid ${GOLD}` }
                  : { color: "#E8DFC5" })}>
                <PLATFORM_PIN.icon className="h-5 w-5" strokeWidth={1.75} />
                <span className="flex-1">{t(PLATFORM_PIN.key)}</span>
              </NavLink>
            )}
            {/* Platform section sub-nav — only rendered on /platform.
                Deep-links via ?tab=<id>. Active-state matches the
                identical URL-derived logic Platform.jsx uses so the
                sidebar highlight and the page content stay in sync.
                In TOP layout mode the sidebar is a horizontal bar with
                no room for sub-items; Platform.jsx renders its own
                horizontal strip instead. */}
            {isPlatformAdmin && location.pathname === "/platform" && (
              <div className="ms-3 mt-1 space-y-0.5 border-s border-white/10 ps-3"
                   data-testid="sidebar-platform-sections">
                {PLATFORM_SECTIONS.map((s) => {
                  const currentTab = new URLSearchParams(location.search).get("tab") || "tenants";
                  const isActive = currentTab === s.id;
                  return (
                    <NavLink key={s.id}
                      to={`/platform${s.id === "tenants" ? "" : `?tab=${s.id}`}`}
                      data-testid={s.testid}
                      className={`flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] font-medium transition-colors ${
                        isActive ? "" : "hover:bg-white/5"
                      }`}
                      style={isActive
                        ? { backgroundColor: "rgba(186,148,74,0.18)", color: GOLD }
                        : { color: "#E8DFC5" }}>
                      <s.icon className="h-4 w-4" strokeWidth={1.75} />
                      <span className="flex-1">{s.labelKey ? t(s.labelKey) : s.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            )}
            {/* Pinned Dashboard row */}
            <NavLink to={DASHBOARD.to} end data-testid={DASHBOARD.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-semibold transition-colors ${
                  isActive ? "" : "hover:bg-white/5"
                }`}
              style={({ isActive }) => (isActive
                ? { backgroundColor: "rgba(186,148,74,0.18)", color: GOLD, borderLeft: `3px solid ${GOLD}` }
                : { color: "#E8DFC5" })}>
              <DASHBOARD.icon className="h-5 w-5" strokeWidth={1.75} />
              <span className="flex-1">{t(DASHBOARD.key)}</span>
            </NavLink>

            {/* Platform-Admin-only: Activity Log deep-link. Kept in
                sync with Platform.jsx via the ?tab= URL param so
                clicking here from any tab lands directly on the log. */}
            {isPlatformAdmin && (
              <NavLink to="/platform?tab=activity-log"
                data-testid="sidebar-activity-log"
                className={({ isActive }) => {
                  const onLogTab = location.pathname === "/platform" &&
                    location.search.includes("tab=activity-log");
                  return `flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-semibold transition-colors ${
                    onLogTab ? "" : "hover:bg-white/5"
                  }`;
                }}
                style={() => {
                  const onLogTab = location.pathname === "/platform" &&
                    location.search.includes("tab=activity-log");
                  return onLogTab
                    ? { backgroundColor: "rgba(186,148,74,0.18)", color: GOLD, borderLeft: `3px solid ${GOLD}` }
                    : { color: "#E8DFC5" };
                }}>
                <History className="h-5 w-5" strokeWidth={1.75} />
                <span className="flex-1">{t("layoutNav.chrome.activity_log")}</span>
              </NavLink>
            )}

            {/* NAVIGATION section label + expand/collapse controls —
                hidden for Platform Admin (no NAV_GROUPS to expand/collapse). */}
            {!isPlatformAdmin && (
              <div className="mt-4 mb-1 flex items-center justify-between px-3">
                <div className="text-[10px] font-semibold uppercase tracking-widest"
                     style={{ color: "#BA944A" }}>
                  {t("layoutNav.chrome.navigation")}
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={expandAll}
                    data-testid="nav-expand-all"
                    className="rounded px-1.5 py-0.5 text-[10px] font-medium hover:bg-white/10"
                    style={{ color: "#C9BFA1" }}>
                    {t("layoutNav.chrome.expand_all")}
                  </button>
                  <span className="text-[10px]" style={{ color: "#5A6F60" }}>·</span>
                  <button type="button" onClick={collapseAll}
                    data-testid="nav-collapse-all"
                    className="rounded px-1.5 py-0.5 text-[10px] font-medium hover:bg-white/10"
                    style={{ color: "#C9BFA1" }}>
                    {t("layoutNav.chrome.collapse_all")}
                  </button>
                </div>
              </div>
            )}

            {/* 7 groups */}
            <SidebarGroups
              visibleGroups={visibleGroups}
              expandedGroups={expandedGroups}
              toggleGroup={toggleGroup}
              t={t} formsProgress={formsProgress} isqmPct={isqmPct}
              visibleItem={visibleItem} activeGroupId={activeGroupId}
              isItemGreyed={isItemGreyed} isItemReviewEnabled={isItemReviewEnabled} />
          </nav>
        </aside>
      )}

      {phoneNavOpen && <div className="phone-backdrop phone-only" onClick={() => setPhoneNavOpen(false)} data-testid="phone-nav-backdrop" aria-hidden="true" />}
      {/* ---------- MAIN COLUMN ---------- */}
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        {/* Top bar — pinned inside the column (main scrolls, header stays put) */}
        {!fullScreen && (
        <header className="app-topbar flex h-14 shrink-0 items-center justify-between border-b bg-white px-6 dark:bg-slate-900 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <button type="button" className="phone-only h-11 w-11 items-center justify-center rounded-md border text-slate-700 dark:text-slate-200"
                    onClick={() => setPhoneNavOpen((v) => !v)} aria-label={t("common.menu")} aria-expanded={phoneNavOpen} data-testid="phone-nav-toggle">
              <Menu className="h-5 w-5" />
            </button>
            {layoutMode === "top" && <CollapseButton variant="header" />}
            <div className="text-xs text-muted-foreground phone-hide">{t("common.selectEng")}:</div>{/* Batch 19 · C3: the label is implied on a phone */}
            <MethodologyVersionBadge engId={currentEngId} />{/* STEP 7e-1 · M3: the badge + its menu (Change methodology version) */}
            <div className="w-72">
              {/* Step 11b · N1: one searchable picker (type to filter, ↑ ↓ Enter, Esc; recent first) — components/EngagementPicker.jsx */}
              <EngagementPicker engagements={engagements} value={currentEngId} userKey={user?.user_id || user?.id}
                                placeholder={t("common.noEng")}
                                onChange={(v) => { confirmLeave(lang).then((ok) => { if (ok) setCurrentEngId(v); }); }} />{/* Batch 28 · Follow-up 1 · F2: asks first while a Save / Cancel bar has unsaved changes */}
            </div>
          </div>
          <div className="app-topbar-right flex items-center gap-2">
            <SearchBar navGroups={searchNavGroups} isReview={(engProfile?.engagement_type || "audit") === "review"} />
            <Button variant="ghost" size="sm" onClick={toggleFullScreen} className="phone-hide gap-1 text-xs"
                    title={t("common.fullScreenHint")} data-testid="fullscreen-toggle">
              <Maximize2 className="h-4 w-4" /><span className="hidden xl:inline">{t("common.fullScreen")}</span>
            </Button>
            <span className="phone-hide"><TimeChip /></span>{/* Batch 19 · C3: the session clock is not needed on a phone */}
            <span className="hidden text-[10px] text-muted-foreground md:inline" title={firmZone()} data-testid="firm-zone-abbr">{firmZoneAbbr()}</span>
            <NotificationsBell />
            <Button variant="ghost" size="sm" onClick={toggle} data-testid="language-toggle" className="gap-1">
              <Languages className="h-4 w-4" />
              <span className="text-xs font-medium">{lang === "en" ? "EN" : "ع"}</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700">
                    {(user?.name || "?").slice(0, 1)}
                  </div>
                  <span className="text-xs">{user?.name}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="text-xs font-medium">{user?.name}</div>
                  <div className="text-[10px] text-muted-foreground">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">
                  <SlidersHorizontal className="me-2 h-4 w-4" /> {t("nav.settings")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="menu-change-password">
                  <KeyRound className="me-2 h-4 w-4" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="me-2 h-4 w-4" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        )}

        {/* Top layout — horizontal nav bar sits BELOW the top bar */}
        {layoutMode === "top" && !navCollapsed && !fullScreen && (
          <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto whitespace-nowrap border-b bg-white px-4 py-2 dark:bg-slate-900 dark:border-slate-800"
                data-testid="top-nav-bar">
            {isPlatformAdmin && (
              <NavLink to={PLATFORM_PIN.to} data-testid={`${PLATFORM_PIN.testid}-top`}
                className={({ isActive }) =>
                  `inline-flex shrink-0 items-center gap-2 rounded-md px-3.5 py-2 text-[15px] font-bold tracking-tight ${
                    isActive
                      ? "bg-slate-900 text-white shadow-sm dark:bg-slate-100 dark:text-slate-900"
                      : "text-slate-900 hover:bg-emerald-50 hover:text-emerald-700 dark:text-slate-100 dark:hover:bg-slate-800"
                  }`}>
                <PLATFORM_PIN.icon className="h-[18px] w-[18px] text-emerald-600" strokeWidth={2} />
                {t(PLATFORM_PIN.key)}
              </NavLink>
            )}
            <NavLink to={DASHBOARD.to} end data-testid={`${DASHBOARD.testid}-top`}
              className={({ isActive }) =>
                `inline-flex shrink-0 items-center gap-2 rounded-md px-3.5 py-2 text-[15px] font-bold tracking-tight ${
                  isActive
                    ? "bg-slate-900 text-white shadow-sm dark:bg-slate-100 dark:text-slate-900"
                    : "text-slate-900 hover:bg-emerald-50 hover:text-emerald-700 dark:text-slate-100 dark:hover:bg-slate-800"
                }`}>
              <DASHBOARD.icon className="h-[18px] w-[18px] text-emerald-600" strokeWidth={2} />
              {t(DASHBOARD.key)}
            </NavLink>
            <div className="mx-2 h-6 w-px bg-slate-200 dark:bg-slate-700" />
            {visibleGroups.map((g) => (
              <TopGroupPill key={g.id} group={g} t={t}
                             formsProgress={formsProgress} isqmPct={isqmPct}
                             visibleItem={visibleItem}
                             isActive={activeGroupId === g.id}
                             isItemGreyed={isItemGreyed}
                             isItemReviewEnabled={isItemReviewEnabled} />
            ))}
            <div className="ms-auto flex items-center gap-2">
              {brandBadge}
              {current && <div className="max-w-xs">{engagementLine}</div>}
              <ToggleButton variant="top" />
            </div>
          </div>
        )}

        {/* Main content scrolls in BOTH directions inside its column so
            the scrollbars sit right at the edge of the visible area
            (not off-screen at document bottom). `min-h-0` lets flex-1
            actually shrink to its allotted height so overflow triggers
            reliably instead of the child pushing the parent taller. */}
        {fullScreen && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-b bg-white/90 px-4 py-1 text-xs dark:bg-slate-900/90 dark:border-slate-800"
               data-testid="fullscreen-bar">
            <span className="me-auto text-[11px] text-muted-foreground">{t("common.fullScreenOnHint")}</span>
            {browserFullScreenAvailable() && (
              <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={toggleBrowserFullScreen} data-testid="browser-fullscreen">
                <Expand className="h-3.5 w-3.5" />{t("common.browserFullScreen")}
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setFullScreen(false)} data-testid="fullscreen-exit">
              <Minimize2 className="h-3.5 w-3.5" />{t("common.exitFullScreen")}
            </Button>
          </div>
        )}
        <main className="main-scroll flex-1 min-h-0 min-w-0 p-6" data-testid="main-content" data-fullscreen={fullScreen ? "1" : "0"}>
          <RestoreMissingBanner />
          <EngagementLocaleBanner />
          <SavedCopyFrame lang={lang}><Outlet /></SavedCopyFrame>{/* Batch 29 · M69: saved work always opens */}
        </main>
      </div>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} forced={mustChange} />
      <PresenceDock />
      <SessionExpiryWarning lang={lang} />
      {logoLightbox}
    </div>
  );
}
